import { PropsWithChildren, ReactElement } from "react";
import tw, { styled } from "twin.macro";

import blackSectionBgSrc from "../../assets/images/black-section-bg.svg";
import blackSectionRowBgSrc from "../../assets/images/black-section-row-bg.svg";

export const SectionHeader = styled.div`
  ${tw`text-center px-[30px] md:px-[70px]`}

  h3 {
    ${tw`my-0 mx-auto`}
  }

  p {
    ${tw`mx-auto`}
  }
`;

export const SectionHeaderNoSubheadline = styled.div`
  ${tw`text-center px-[30px] md:px-[70px] pb-[20px] md:pb-[30px]`}

  h3 {
    ${tw`my-0 mx-auto`}
  }
`;

export const SectionContent = styled.div`
  ${tw`px-[30px] md:px-[70px] pt-[30px]`}
`;

const SectionCtaWrapper = styled.div`
  ${tw`px-[30px] md:px-[70px] mt-[50px] flex flex-col justify-center items-center text-center`}
  gap: 50px;

  h3 {
    ${tw`m-0`}
  }
`;

const SectionCtaButtons = styled.div`
  ${tw`flex gap-5 justify-center items-center`}

  @media (max-width: 500px) {
    ${tw`flex-wrap`}
  }
`;

interface SectionCtaProps {
  title?: ReactElement;
  buttons: ReactElement;
}

export const SectionCta: React.FunctionComponent<
  PropsWithChildren<SectionCtaProps>
> = ({ title, buttons, ...props }) => (
  <SectionCtaWrapper {...props}>
    {title}
    <SectionCtaButtons>{buttons}</SectionCtaButtons>
  </SectionCtaWrapper>
);

const SectionWrapper = styled.div`
  ${tw`rounded-[40px] my-[50px] md:my-[80px] py-[50px] md:py-[70px]`}

  &.white {
    ${tw`py-0 md:py-0`}
  }

  &.gray {
    ${tw`bg-[#f7f8f9]`}
  }

  &.black {
    background-color: #020711;
    background-image: url(${blackSectionBgSrc});
    background-repeat: no-repeat;
    background-size: 100% 320px;
    background-position: bottom left;
  }
`;

export const SectionHeadline = styled.h3`
  ${tw`font-title font-normal text-inherit text-title`}

  font-size: 36px;
  line-height: 48px;

  @media (max-width: 767px) {
    font-size: 28px;
    line-height: 42px;
  }
`;

export const SectionHeadlineWhite = styled(SectionHeadline)`
  ${tw`text-white`}
`;

export const SectionHeadlineHighlight = styled.span`
  ${tw`relative text-theme-blue-1 font-bold`}
`;

export const SectionSubheadline = styled.p`
  ${tw`font-subtitle font-normal self-stretch mt-[24px]`}

  font-size: 21px;
  line-height: 39px;

  @media (max-width: 767px) {
    font-size: 18px;
    line-height: 32px;
  }
`;

export const SectionSubheadlineWhite = styled(SectionSubheadline)`
  ${tw`text-white`}
`;

export const BlackSectionRows = styled.div`
  ${tw`flex flex-col`}
  gap: 40px;
`;

export const BlackSectionRow = styled.div<{ even?: boolean }>`
  ${tw`flex flex-col items-center gap-[50px] p-[60px]`}
  ${tw`lg:flex-row lg:flex-nowrap lg:px-[60px] lg:py-0 lg:gap-[60px]`}
  ${tw`rounded-[40px] relative overflow-hidden z-0`}

  background-color: #3F3F46;
  background-image: url(${blackSectionRowBgSrc});
  background-repeat: no-repeat;
  background-size: 100% 382px;
  background-position: bottom left;

  &:before {
    content: "";
    ${tw`block w-[540px] h-[540px] bg-[#3F3F46] rounded-full absolute z-0`}
    ${tw`hidden lg:block`}
    top: calc(50% - 270px);

    ${(props) => (props.even ? tw`lg:left-[-131px]` : tw`lg:right-[-131px]`)}
  }
`;

export const BlackSectionRowContent = styled.div`
  ${tw`text-center z-1`}
  ${tw`lg:text-left lg:py-[100px] lg:basis-[50%] lg:flex-grow-0`}

  p {
    ${tw`mb-0`}
  }
`;

export const BlackSectionRowImage = styled.div<{ even?: boolean }>`
  ${tw`max-w-[370px] z-1 -order-1 lg:order-1`}
  ${tw`lg:basis-[50%] lg:flex-grow lg:flex lg:flex-col lg:items-center`}

  ${(props) => (props.even ? tw`lg:-order-1` : "")}
`;

export const BlackSectionIntegrations = styled.div`
  ${tw`flex flex-col justify-center items-center text-center mt-[60px] px-[30px]`}
`;

export const BlackSectionIntegrationsHeadline = styled.h4`
  ${tw`font-title font-semibold text-center text-white text-[28px] leading-[40px]`}
`;
export const BlackSectionIntegrationsSubheadline = styled.p`
  ${tw`text-center text-white text-[24px] leading-[36px] mx-auto mb-[40px] md:mb-[50px]`}
`;

export enum SectionColor {
  White = "white",
  Gray = "gray",
  Black = "black",
}

interface SectionProps {
  color?: SectionColor;
}

const Section: React.FunctionComponent<PropsWithChildren<SectionProps>> = ({
  children,
  color = SectionColor.White,
  ...props
}) => (
  <SectionWrapper className={color} {...props}>
    {children}
  </SectionWrapper>
);

export default Section;
